import { motion } from "framer-motion";
import {
  PhoneCall, Handshake, CalendarCheck2, ReceiptText, LineChart, TrendingUp, Sparkles, Compass,
} from "lucide-react";

/**
 * The Executive AI core — a glowing neural intelligence surrounded by capability tiles.
 * All SVG + CSS. Not a robot. Not a brain photo. An abstract intelligence engine
 * with hexagonal geometry, orbit rings, and calligraphic connector lines.
 */
export default function ExecAICore() {
  return (
    <div className="relative w-full aspect-[10/9] max-w-[980px]">
      {/* Ambient glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(closest-side at 50% 50%, rgba(47,203,169,0.20), transparent 60%), radial-gradient(closest-side at 50% 50%, rgba(240,195,74,0.08), transparent 70%)",
        }}
      />

      {/* Central core */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <CoreOrb />
      </div>

      {/* Orbit rings */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 720 640" fill="none">
        <defs>
          <linearGradient id="ringGrad" x1="0" x2="1">
            <stop offset="0%" stopColor="#2FCBA9" stopOpacity="0" />
            <stop offset="50%" stopColor="#2FCBA9" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#2FCBA9" stopOpacity="0" />
          </linearGradient>
          <radialGradient id="glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0%" stopColor="#2FCBA9" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#2FCBA9" stopOpacity="0" />
          </radialGradient>
        </defs>
        <ellipse cx="360" cy="320" rx="260" ry="150" stroke="url(#ringGrad)" strokeWidth="1"/>
        <ellipse cx="360" cy="320" rx="180" ry="105" stroke="rgba(47,203,169,0.14)" strokeWidth="1" strokeDasharray="3 6"/>
        <ellipse cx="360" cy="320" rx="110" ry="65" stroke="rgba(240,195,74,0.14)" strokeWidth="1" strokeDasharray="2 4"/>

        {/* Calligraphic connector lines to tile anchor points */}
        {CONNECTOR_LINES.map((p, i) => (
          <path key={i} d={p} stroke="rgba(47,203,169,0.28)" strokeWidth="1" fill="none" />
        ))}
      </svg>

      {/* Capability tiles */}
      {TILES.map((t, i) => (
        <motion.div
          key={t.label}
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.55, delay: 0.06 * i }}
          className={`absolute ${t.pos} exec-card px-3.5 py-2.5 flex items-center gap-2.5 ${t.float}`}
          data-testid={`solution-tile-${i}`}
        >
          <div className="w-8 h-8 rounded-lg bg-exec-emerald-3/40 border border-exec-emerald/25 grid place-items-center shrink-0">
            <t.icon className="w-4 h-4 text-exec-emerald-2" strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <div className="text-[12.5px] text-exec-ink font-medium leading-tight">{t.label}</div>
            <div className="text-[10.5px] text-exec-mute mt-0.5">{t.sub}</div>
          </div>
        </motion.div>
      ))}
    </div>
  );
}

/* ---- Core orb ---- */
function CoreOrb() {
  return (
    <div className="relative w-[180px] h-[180px]">
      {/* Outer glow */}
      <div className="absolute -inset-8 exec-core-glow rounded-full" />
      {/* Rotating hexagonal frame */}
      <motion.div
        className="absolute inset-0"
        animate={{ rotate: 360 }}
        transition={{ duration: 60, ease: "linear", repeat: Infinity }}
      >
        <svg viewBox="0 0 180 180" className="w-full h-full">
          <polygon
            points="90,10 158,50 158,130 90,170 22,130 22,50"
            fill="none" stroke="rgba(47,203,169,0.35)" strokeWidth="1"
          />
          <polygon
            points="90,30 141,60 141,120 90,150 39,120 39,60"
            fill="none" stroke="rgba(47,203,169,0.20)" strokeWidth="1" strokeDasharray="3 4"
          />
        </svg>
      </motion.div>
      {/* Counter-rotating inner ring */}
      <motion.div
        className="absolute inset-6"
        animate={{ rotate: -360 }}
        transition={{ duration: 40, ease: "linear", repeat: Infinity }}
      >
        <svg viewBox="0 0 140 140" className="w-full h-full">
          <circle cx="70" cy="70" r="60" fill="none" stroke="rgba(240,195,74,0.28)" strokeWidth="1" strokeDasharray="4 6"/>
        </svg>
      </motion.div>
      {/* Inner sphere */}
      <div
        className="absolute inset-[52px] rounded-full"
        style={{
          background:
            "radial-gradient(circle at 40% 35%, #7EF0D3 0%, #2FCBA9 40%, #0F5E52 80%)",
          boxShadow:
            "inset 0 8px 20px rgba(255,255,255,0.15), inset 0 -10px 30px rgba(0,0,0,0.35), 0 0 60px rgba(47,203,169,0.55)",
        }}
      />
      {/* Sparkle icon inside */}
      <div className="absolute inset-0 grid place-items-center">
        <Sparkles className="w-5 h-5 text-white/90" strokeWidth={1.6} />
      </div>
      {/* Label under */}
      <div className="absolute -bottom-14 inset-x-0 text-center">
        <div className="text-[10px] tracking-[0.24em] uppercase text-exec-emerald-2 font-medium">
          Velque Executive AI
        </div>
        <div className="text-[10.5px] mt-1 italic text-[#D4A017]">
          One executive. Every operation.
        </div>
      </div>
    </div>
  );
}

const TILES = [
  { label: "AI Voice Receptionist",    sub: "24/7 customer handling", icon: PhoneCall,       pos: "top-6 left-2",         float: "exec-float-slow" },
  { label: "Vendor Intelligence",      sub: "Track · Remind · Pay",   icon: Handshake,       pos: "top-6 right-2",        float: "exec-float-med"  },
  { label: "Appointment Management",   sub: "Book · Reschedule · Remind", icon: CalendarCheck2, pos: "top-1/2 -translate-y-1/2 left-0", float: "exec-float-fast" },
  { label: "Invoice Management",       sub: "Create · Send · Collect", icon: ReceiptText,    pos: "top-1/2 -translate-y-1/2 right-0", float: "exec-float-slow" },
  { label: "Cash Flow Monitoring",     sub: "Live health signals",     icon: LineChart,      pos: "bottom-8 left-2",       float: "exec-float-med"  },
  { label: "Expense Intelligence",     sub: "Track · Analyze",         icon: TrendingUp,     pos: "bottom-8 right-2",      float: "exec-float-fast" },
  { label: "Daily Executive Brief",    sub: "Priorities made clear",   icon: Compass,        pos: "top-2 left-[35%] -translate-x-1/2", float: "exec-float-slow" },
];

/* Calligraphic bezier connectors from tile anchors to core center */
const CX = 360, CY = 320;
const CONNECTOR_LINES = [
  `M 80 90  C 200 140, 260 260, ${CX} ${CY}`,
  `M 640 90 C 520 140, 460 260, ${CX} ${CY}`,
  `M 40 320 C 160 320, 240 320, ${CX} ${CY}`,
  `M 680 320 C 560 320, 480 320, ${CX} ${CY}`,
  `M 80 560 C 200 500, 260 380, ${CX} ${CY}`,
  `M 640 560 C 520 500, 460 380, ${CX} ${CY}`,
  `M 360 60  C 360 140, 360 220, ${CX} ${CY}`,
];
